import { useEffect, useCallback } from 'react'
import type L from 'leaflet'

interface UseMapViewPersistenceProps {
  mapRef: React.MutableRefObject<L.Map | null>
  isInitializedRef: React.MutableRefObject<boolean>
  onSaveView: (view: { center: [number, number]; zoom: number }) => void
}

export const useMapViewPersistence = ({
  mapRef,
  isInitializedRef,
  onSaveView,
}: UseMapViewPersistenceProps) => {
  const saveCurrentView = useCallback(() => {
    if (!mapRef.current || !isInitializedRef.current) return

    try {
      const center = mapRef.current.getCenter()
      const zoom = mapRef.current.getZoom()
      
      if (center && typeof zoom === 'number') {
        onSaveView({
          center: [center.lat, center.lng],
          zoom,
        })
      }
    } catch (error) {
      console.warn('Failed to save map view:', error)
    }
  }, [mapRef, isInitializedRef, onSaveView])

  useEffect(() => {
    if (!mapRef.current || !isInitializedRef.current) return

    const map = mapRef.current

    // Save on moveend (after panning/dragging) and zoomend
    map.on('moveend', saveCurrentView)
    map.on('zoomend', saveCurrentView)

    return () => {
      map.off('moveend', saveCurrentView)
      map.off('zoomend', saveCurrentView)
    }
  }, [mapRef, isInitializedRef, saveCurrentView])

  return { saveCurrentView }
}
